import React from 'react';
import { createPortal } from 'react-dom';
import { ModalHeader, ModalBody, Row, Col, Label } from 'reactstrap';
import Rodal from '../Rodal/Rodal';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import Helpers from '../../utils/helper';
import { areValuesEqual } from '../../utils/typedHelper';
import './SynchronizationConfirmationDialog.scss';

interface SynchronizationConfirmationDialogProps {
    isVisible: boolean;
    setIsVisible: (isVisible: boolean) => void;
    previousFteDvm?: number;
    previousLtmRevenue?: number;
    newFteDvm?: number;
    newLtmRevenue?: number;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
}

export const SynchronizationConfirmationDialog = (
    props: SynchronizationConfirmationDialogProps
) => {
    const hasFteDvm = props.previousFteDvm !== undefined && props.newFteDvm !== undefined;
    const hasLtmRevenue = props.previousLtmRevenue !== undefined && props.newLtmRevenue !== undefined;
    
    const hasFteDvmChange = hasFteDvm && !areValuesEqual(props.previousFteDvm, props.newFteDvm);
    const hasLtmRevenueChange = hasLtmRevenue && !areValuesEqual(props.previousLtmRevenue, props.newLtmRevenue);
    
    const hasAnyChange = hasFteDvmChange || hasLtmRevenueChange;

    if (!props.isVisible || !hasAnyChange) {
        return null;
    }

    const dialogContent = (
        <Rodal
            visible={true}
            animation="slideTop"
            width={400}
            showCloseButton={false}
            onClose={() => {
                props.setIsVisible(false);
                props.onCancel();
            }}
            showMask={false}
            className="synchronization-confirmation-dialog">
            <ModalHeader>Update your practice profile with this new data?</ModalHeader>
            <ModalBody>
                {hasAnyChange && (
                    <Row>
                        <Col sm={12}>
                            <div className="synchronization-details">
                                {hasFteDvmChange && (
                                    <div className="detail-row">
                                        <div className="detail-label">
                                            FTE DVM:
                                        </div>
                                        <div className="detail-value">
                                            <span className="old-value">
                                                {props.previousFteDvm}
                                            </span>
                                            <span className="arrow">→</span>
                                            <span className="new-value">
                                                {props.newFteDvm}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {hasLtmRevenueChange && (
                                    <div className="detail-row">
                                        <div className="detail-label">
                                            LTM Revenue:
                                        </div>
                                        <div className="detail-value">
                                            <span>
                                                {Helpers.GetCurrencyFormat(
                                                    props.previousLtmRevenue ?? 0,
                                                    0,
                                                    false
                                                )}
                                            </span>
                                            <span className="arrow">→</span>
                                            <span>
                                                {Helpers.GetCurrencyFormat(
                                                    props.newLtmRevenue ?? 0,
                                                    0,
                                                    false
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}
                <div className="buttons-wrapper">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            props.onCancel();
                        }}
                        disabled={props.isLoading}>
                        No
                    </button>
                    <ButtonLoader
                        className="btn btn-primary"
                        disabled={false}
                        buttonText="Yes"
                        loaderButtonText="Yes"
                        isLoading={props.isLoading}
                        onClick={() => {
                            props.onConfirm();
                        }}
                    />
                </div>
            </ModalBody>
        </Rodal>
    );

    return createPortal(dialogContent, document.body);
};
